import React from 'react';
import { useHistory } from 'react-router-dom';
import { PreviewLocation } from 'src/components/Locations';
import MapHeaderQuote from 'src/components/MapHeaderQuote';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  Label,
  Row,
  useApp
} from 'straightline-ui';
import { toast } from 'straightline-ui';
import { MODE } from 'straightline-utils/constants';

export default function NewAddressDetail() {
  const app = useApp();
  const schema = app.service('api/address-details').schema;
  const { create } = app.service('api/address-details').useCreate();
  const history = useHistory();

  function onSubmit(data, formik) {
    create(data)
      .then((addressDetail) => {
        formik.setSubmitting(false);
        toast.success('New address created!');
        history.push(`/addresses/${addressDetail.id}`);
      })
      .catch((err) => {
        toast.errors(err);
        if (err.errors) {
          formik.setErrors(err.errors);
        }
        formik.setSubmitting(false);
      });
  }

  return (
    <React.Fragment>
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          name: undefined,
          address: {
            address_1: undefined,
            address_2: undefined,
            city: undefined,
            state: undefined,
            postal_code: undefined,
            country: undefined,
            name: undefined,
            contact: undefined,
            email: undefined,
            phone: undefined,
            appointment: undefined,
            latitude: undefined,
            longitude: undefined
          },
          description: undefined,
          default_address: undefined
        }}
        validationSchema={schema}
        render={({ isSubmitting, values, setValues }) => {
          const mapAddress = values.address?.address_1
            ? {
                ...values.address,
                position: {
                  lat: Number(values.address.latitude),
                  lng: Number(values.address.longitude)
                }
              }
            : undefined;

          return (
            <React.Fragment>
              <MapHeaderQuote
                quote={{
                  pickup_address: mapAddress,
                  mode: MODE.FTL
                }}
              />
              <Container className="pb-3" fluid="md">
                <Card className="mt-n6">
                  <CardBody className="px-3 px-md-4">
                    <Form>
                      <Row>
                        <Col lg="6" className="mb-4 mb-lg-0">
                          <FormGroup>
                            <Label>Address Name*</Label>
                            <FormInput
                              name="name"
                              disabled={isSubmitting}
                              placeholder="Enter your name for this address"
                            />
                            <FormFeedback name="name" />
                            <div className="text-muted mt-2">
                              This is a nickname for this address. It is used
                              when searching for saved address but is not shown
                              on the shipment or to carriers.
                            </div>
                          </FormGroup>
                          <FormGroup>
                            <Label>Address*</Label>
                            <FormInput
                              useAddress2
                              type="address"
                              name="address"
                              disabled={isSubmitting}
                              placeholder="Enter an address"
                              initialValue={values.address}
                              addressToOption={(address) => {
                                if (!address || !address.address_1) {
                                  return undefined;
                                }
                                return {
                                  label: `${address.address_1}, ${address.city}, ${address.state}, ${address.postal_code}`,
                                  value: address
                                };
                              }}
                              onChange={({ target: { value } }) => {
                                if (!value) {
                                  return setValues({
                                    ...values,
                                    address: null
                                  });
                                }

                                setValues({
                                  ...values,
                                  address: {
                                    ...values.address,
                                    ...value,
                                    name:
                                      values.address?.name ||
                                      value.name ||
                                      undefined
                                  }
                                });
                              }}
                            />
                            <FormFeedback name="address.address_1" />
                          </FormGroup>
                          <FormGroup>
                            <Label>Company Name*</Label>
                            <FormInput
                              name="address.name"
                              disabled={isSubmitting}
                              placeholder="Enter Company or Location name"
                            />
                            <FormFeedback name="address.name" />
                          </FormGroup>
                          <FormGroup>
                            <Label>Contact Name</Label>
                            <FormInput
                              name="address.contact"
                              disabled={isSubmitting}
                              placeholder="Enter Contact Name"
                            />
                            <FormFeedback name="address.contact" />
                          </FormGroup>
                          <FormGroup>
                            <Label>Contact Phone</Label>
                            <FormInput
                              name="address.phone"
                              type="phone"
                              disabled={isSubmitting}
                              placeholder="Enter Contact Phone"
                            />
                            <FormFeedback name="address.phone" />
                          </FormGroup>
                          <FormGroup>
                            <Label>Contact Email</Label>
                            <FormInput
                              name="address.email"
                              disabled={isSubmitting}
                              placeholder="Enter Contact Email"
                            />
                            <FormFeedback name="address.email" />
                          </FormGroup>
                          <FormGroup>
                            <Label>Description</Label>
                            <FormInput
                              name="description"
                              type="textarea"
                              rows="4"
                              disabled={isSubmitting}
                              placeholder="Enter details or notes about the address"
                            />
                            <FormFeedback name="description" />
                          </FormGroup>
                          <FormGroup>
                            <Label>Default Appointment</Label>
                            <FormInput
                              name="address.appointment"
                              rows="4"
                              disabled={isSubmitting}
                              placeholder="Enter a default appointment"
                            />
                            <FormFeedback name="address.appointment" />
                          </FormGroup>
                          <FormGroup>
                            <Label>Default Address</Label>
                            <FormInput
                              name="default_address"
                              type="radio-buttons"
                              disabled={isSubmitting}
                              emptyValue={null}
                              options={[
                                { label: 'Pickup', value: 'pickup' },
                                { label: 'Delivery', value: 'delivery' }
                              ]}
                              onChange={({ target: { value } }) => {
                                setValues({
                                  ...values,
                                  default_address:
                                    value === values.default_address
                                      ? null
                                      : value
                                });
                              }}
                            />
                            <div className="text-secondary mt-2">
                              Default address will automatically be applied to
                              new quotes.
                            </div>
                          </FormGroup>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                            className={
                              isSubmitting ? 'is-loading px-6' : 'px-6'
                            }
                          >
                            Submit
                          </Button>
                        </Col>
                        <Col lg="6">
                          <PreviewLocation values={values} />
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>
              </Container>
            </React.Fragment>
          );
        }}
      />
    </React.Fragment>
  );
}
